import React, { useEffect, useRef, useState } from 'react';
import {
  PT_WHEN_CANCEL,
  PT_WHEN_CREATE,
  PT_WHEN_NEW_PLACEHOLDER,
  PT_WHEN_NONE,
  PT_WHEN_OFFER,
  PT_WHEN_OFFER_ARIA,
  PT_WHEN_SAVE,
  PT_WHEN_SELECT_ARIA,
} from '../../utils/writerCopy';

const CREATE = '__create__';

/**
 * One scene's When (spec: docs/plottrack/plot-track-time-people-and-canon-room-spec.md §1).
 * A dropdown of the book's own labels plus "Create new…". When the scene's plan states a
 * year outright, an offer button sets it on the writer's click; nothing is set silently.
 */
export default function PlotTrackWhenCell({ sceneName, value, offer, labels, onSet, onCreate }) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  // Run 62: the box opened under a scene scrolled out of view (or under the
  // pinned thread names), so the writer couldn't see where to type.
  useEffect(() => {
    if (creating && boxRef.current?.scrollIntoView) {
      boxRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [creating]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const save = () => run(async () => {
    const text = draft.trim();
    if (!text) return;
    const created = await onCreate(text);
    if (created?.id) await onSet(created.id);
    setDraft('');
    setCreating(false);
  });

  const takeOffer = () => run(async () => {
    const id = offer.label_id || (await onCreate(offer.label))?.id;
    if (id) await onSet(id);
  });

  if (creating) {
    return (
      <div className="pt-when pt-when--new" ref={boxRef}>
        <input
          type="text"
          className="pt-when__input"
          value={draft}
          placeholder={PT_WHEN_NEW_PLACEHOLDER}
          aria-label={PT_WHEN_CREATE}
          maxLength={80}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setCreating(false);
          }}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <button type="button" className="pt-when__btn" disabled={busy || !draft.trim()} onClick={save}>{PT_WHEN_SAVE}</button>
        <button type="button" className="pt-when__btn pt-when__btn--quiet" disabled={busy} onClick={() => setCreating(false)}>{PT_WHEN_CANCEL}</button>
      </div>
    );
  }

  return (
    <div className="pt-when">
      <select
        className="pt-when__select"
        value={value || ''}
        aria-label={PT_WHEN_SELECT_ARIA(sceneName)}
        disabled={busy}
        onChange={(e) => {
          const next = e.target.value;
          if (next === CREATE) { setCreating(true); return; }
          run(() => onSet(next || null));
        }}
      >
        <option value="">{PT_WHEN_NONE}</option>
        {(labels || []).map((label) => (
          <option key={label.id} value={label.id}>{label.label}</option>
        ))}
        <option value={CREATE}>{PT_WHEN_CREATE}</option>
      </select>
      {!value && offer?.label && (
        <button
          type="button"
          className="pt-when__offer"
          disabled={busy}
          aria-label={PT_WHEN_OFFER_ARIA(offer.label)}
          onClick={takeOffer}
        >
          {PT_WHEN_OFFER(offer.label)}
        </button>
      )}
    </div>
  );
}
